import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Loader2,
  Zap,
  Play,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { apiRequest } from '../lib/api';

interface Job {
  id: string;
  name: string;
  description: string;
  status: 'idle' | 'running' | 'completed' | 'failed';
  last_run: string | null;
  last_duration_ms: number | null;
  last_error: string | null;
  schedule: string | null;
  is_enabled: boolean;
}

const jobDefinitions: Omit<Job, 'status' | 'last_run' | 'last_duration_ms' | 'last_error'>[] = [
  {
    id: 'process-earnings',
    name: 'Process Referral Earnings',
    description: 'Calculate and distribute referral commissions for recent trades',
    schedule: 'Every 5 minutes',
    is_enabled: true,
  },
  {
    id: 'update-fee-tiers',
    name: 'Update User Fee Tiers',
    description: 'Auto-upgrade users based on 30-day trading volume',
    schedule: 'Daily at midnight',
    is_enabled: true,
  },
  {
    id: 'convert-referrals',
    name: 'Check Referral Conversions',
    description: 'Convert pending referrals when users meet volume threshold',
    schedule: 'Every 15 minutes',
    is_enabled: true,
  },
  {
    id: 'cleanup-expired',
    name: 'Cleanup Expired Data',
    description: 'Remove expired referral codes and stale tracking data',
    schedule: 'Weekly on Sunday',
    is_enabled: true,
  },
  {
    id: 'sync-trading-stats',
    name: 'Sync Trading Statistics',
    description: 'Aggregate trading metrics for dashboard analytics',
    schedule: 'Hourly',
    is_enabled: true,
  },
  {
    id: 'generate-reports',
    name: 'Generate Reports',
    description: 'Create daily/weekly reports for admins',
    schedule: 'Daily at 6 AM',
    is_enabled: false,
  },
];

function StatusBadge({ status }: { status: Job['status'] }) {
  const config = {
    idle: { icon: Clock, bg: 'bg-zinc-500/20', text: 'text-zinc-400', label: 'Idle' },
    running: { icon: RefreshCw, bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Running' },
    completed: { icon: CheckCircle, bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Completed' },
    failed: { icon: XCircle, bg: 'bg-red-500/20', text: 'text-red-400', label: 'Failed' },
  };

  const { icon: Icon, bg, text, label } = config[status];

  return (
    <span className={`flex items-center gap-1 text-xs px-2 py-0.5 ${bg} ${text}`}>
      <Icon className={`w-3 h-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      {label}
    </span>
  );
}

export default function Jobs() {
  const queryClient = useQueryClient();
  const [runningJobs, setRunningJobs] = useState<Set<string>>(new Set());

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin-jobs'],
    queryFn: async () => {
      const result = await apiRequest('GET', '/api/admin/jobs');
      // Merge with definitions if API returns partial data
      return result.jobs || jobDefinitions.map(job => ({
        ...job,
        status: 'idle' as const,
        last_run: null,
        last_duration_ms: null,
        last_error: null,
      }));
    },
    refetchInterval: 5000, // Poll every 5 seconds to check job status
  });

  const runJobMutation = useMutation({
    mutationFn: (jobId: string) => apiRequest('POST', `/api/admin/jobs/${jobId}/run`),
    onMutate: (jobId) => {
      setRunningJobs(prev => new Set(prev).add(jobId));
    },
    onSuccess: () => {
      toast.success('Job started successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-jobs'] });
    },
    onError: (error: any, failedJobId) => {
      toast.error(error.message || 'Failed to run job');
      setRunningJobs(prev => {
        const next = new Set(prev);
        next.delete(failedJobId);
        return next;
      });
    },
    onSettled: (_, __, settledJobId) => {
      // Remove from running after a delay to allow status update
      setTimeout(() => {
        setRunningJobs(prev => {
          const next = new Set(prev);
          next.delete(settledJobId);
          return next;
        });
      }, 2000);
    },
  });

  const toggleJobMutation = useMutation({
    mutationFn: ({ jobId, enabled }: { jobId: string; enabled: boolean }) =>
      apiRequest('PUT', `/api/admin/jobs/${jobId}`, { is_enabled: enabled }),
    onSuccess: () => {
      toast.success('Job updated successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-jobs'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update job');
    },
  });

  const jobs = data || jobDefinitions.map(job => ({
    ...job,
    status: 'idle' as const,
    last_run: null,
    last_duration_ms: null,
    last_error: null,
  }));

  const formatDuration = (ms: number | null) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatLastRun = (date: string | null) => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-medium text-white">Background Jobs</h1>
          <p className="text-sm text-zinc-500">Manage and monitor automated tasks</p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-400 hover:text-white
            border border-white/10 hover:bg-white/5"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map((job: Job) => {
            const isRunning = runningJobs.has(job.id) || job.status === 'running';

            return (
              <div
                key={job.id}
                className={`border border-white/10 p-4 ${!job.is_enabled ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 ${job.is_enabled ? 'bg-emerald-500/10' : 'bg-zinc-500/10'}`}>
                      <Zap className={`w-4 h-4 ${job.is_enabled ? 'text-emerald-500' : 'text-zinc-500'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium text-white">{job.name}</h3>
                        <StatusBadge status={isRunning ? 'running' : job.status} />
                      </div>
                      <p className="text-xs text-zinc-500 mt-0.5">{job.description}</p>

                      <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {job.schedule}
                        </span>
                        <span>Last run: {formatLastRun(job.last_run)}</span>
                        {job.last_duration_ms && (
                          <span>Duration: {formatDuration(job.last_duration_ms)}</span>
                        )}
                      </div>

                      {job.last_error && (
                        <div className="flex items-start gap-1 mt-2 text-xs text-red-400">
                          <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-1">{job.last_error}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleJobMutation.mutate({ jobId: job.id, enabled: !job.is_enabled })}
                      className={`px-3 py-1.5 text-xs border ${
                        job.is_enabled
                          ? 'border-zinc-600 text-zinc-400 hover:text-white hover:border-zinc-500'
                          : 'border-emerald-600 text-emerald-400 hover:bg-emerald-600/10'
                      }`}
                    >
                      {job.is_enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => runJobMutation.mutate(job.id)}
                      disabled={isRunning || !job.is_enabled}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700
                        text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isRunning ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Play className="w-3 h-3" />
                      )}
                      Run Now
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info Panel */}
      <div className="mt-6 p-4 border border-white/10 bg-white/5">
        <h3 className="text-xs font-medium text-white mb-2">About Background Jobs</h3>
        <ul className="text-xs text-zinc-500 space-y-1">
          <li>Jobs run automatically according to their schedule when enabled.</li>
          <li>Use "Run Now" to manually trigger a job outside its schedule.</li>
          <li>Disabling a job prevents it from running automatically but allows manual runs.</li>
          <li>Job status updates automatically every 5 seconds.</li>
        </ul>
      </div>
    </div>
  );
}
